import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const HeaderContainer = styled.header`
  background-color: var(--white);
  box-shadow: var(--shadow-sm);
  position: sticky;
  top: 0;
  z-index: var(--z-index-sticky);
  width: 100%;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled(Link)`
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--primary);
  display: flex;
  align-items: center;
  text-decoration: none;
  
  img {
    height: 40px;
  }
`;

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--white);
    flex-direction: column;
    align-items: flex-start;
    padding: var(--spacing-md);
    box-shadow: var(--shadow-md);
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavItem = styled.li`
  position: relative;
  margin: 0 var(--spacing-md);
  
  @media (max-width: 768px) {
    margin: var(--spacing-xs) 0;
    width: 100%;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-primary);
  font-weight: 500;
  padding: var(--spacing-sm);
  transition: color var(--transition-fast);
  text-decoration: none;
  
  &:hover {
    color: var(--primary);
  }
  
  @media (max-width: 768px) {
    display: block;
    padding: var(--spacing-sm) 0;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-lg);
  min-width: 200px;
  padding: var(--spacing-md);
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: var(--z-index-dropdown);
  
  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    padding: var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-md);
    border-left: 2px solid var(--primary-light);
    margin-top: var(--spacing-xs);
  }
`;

const DropdownLink = styled(Link)`
  display: block;
  color: var(--text-primary);
  padding: var(--spacing-sm);
  transition: color var(--transition-fast);
  text-decoration: none;
  
  &:hover {
    color: var(--primary);
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-top: var(--spacing-md);
    width: 100%;
  }
`;

const DemoButton = styled(Link)`
  background-color: var(--primary);
  color: var(--white);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-md);
  font-weight: 600;
  transition: background-color var(--transition-fast);
  text-decoration: none;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: var(--font-size-xl);
  color: var(--primary);
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const toggleDropdown = (dropdown) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };
  
  const closeDropdowns = () => {
    setActiveDropdown(null);
  };
  
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">
          Leadfy
        </Logo>
        
        <MobileMenuButton onClick={toggleMobileMenu}>
          {mobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <NavContainer isOpen={mobileMenuOpen}>
          <NavList>
            <NavItem>
              <NavLink 
                to="#" 
                onClick={(e) => {
                  e.preventDefault();
                  toggleDropdown('solutions');
                }}
              >
                Soluções
              </NavLink>
              <DropdownContainer isOpen={activeDropdown === 'solutions'}>
                <DropdownLink to="/solutions/cx-teams">Equipes de CX</DropdownLink>
                <DropdownLink to="/solutions/support-agents">Agentes de Suporte</DropdownLink>
                <DropdownLink to="/solutions/operations">Operações</DropdownLink>
                <DropdownLink to="/solutions/security-it">Segurança & TI</DropdownLink>
                <DropdownLink to="/solutions/content-management">Gestão de Conteúdo</DropdownLink>
                <DropdownLink to="/solutions/saas">SaaS</DropdownLink>
                <DropdownLink to="/solutions/ecommerce-retail">E-commerce & Varejo</DropdownLink>
                <DropdownLink to="/solutions/fintech">Fintech</DropdownLink>
                <DropdownLink to="/solutions/healthcare">Saúde</DropdownLink>
                <DropdownLink to="/solutions/mobile-apps">Apps Móveis</DropdownLink>
              </DropdownContainer>
            </NavItem>
            
            <NavItem>
              <NavLink 
                to="#" 
                onClick={(e) => {
                  e.preventDefault();
                  toggleDropdown('agents');
                }}
              >
                Agentes
              </NavLink>
              <DropdownContainer isOpen={activeDropdown === 'agents'}>
                <DropdownLink to="/agents/discover">Discover</DropdownLink>
                <DropdownLink to="/agents/solve">Solve</DropdownLink>
                <DropdownLink to="/agents/triage">Triage</DropdownLink>
                <DropdownLink to="/agents/assist">Assist</DropdownLink>
                <DropdownLink to="/security">Segurança & Compliance</DropdownLink>
                <DropdownLink to="/integrations">Integrações</DropdownLink>
              </DropdownContainer>
            </NavItem>
            
            <NavItem>
              <NavLink 
                to="#" 
                onClick={(e) => {
                  e.preventDefault();
                  toggleDropdown('resources');
                }}
              >
                Recursos
              </NavLink>
              <DropdownContainer isOpen={activeDropdown === 'resources'}>
                <DropdownLink to="/blog">Blog</DropdownLink>
                <DropdownLink to="/webinars">Webinars</DropdownLink>
                <DropdownLink to="/customer-stories">Casos de Clientes</DropdownLink>
                <DropdownLink to="/support">Suporte</DropdownLink>
                <DropdownLink to="/faqs">FAQs</DropdownLink>
              </DropdownContainer>
            </NavItem>
            
            <NavItem>
              <NavLink to="/pricing">Preços</NavLink>
            </NavItem>
          </NavList>
          
          <ButtonsContainer>
            <DemoButton to="/request-demo">Solicitar Demo</DemoButton>
          </ButtonsContainer>
        </NavContainer>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
